// Tiny path-scoped observable store with an intent bus. ~80 LOC of behaviour.
//
// API (matches the legacy JS surface):
//   const store = createStore(initialState);
//   store.get('a.b.c')                    -> value (undefined if missing)
//   store.set('a.b.c', value)             -> notify subscribers
//   store.set('a.b', { c: 1, d: 2 })      -> shallow-merge object into a.b
//   const off = store.subscribe(path, cb) -> notifies on path or any ancestor/descendant change
//   store.dispatch(intent, payload)       -> calls registered intent handlers
//   const off = store.onIntent(intent, fn)
//
// Subscription match rule: notify if setPath is a prefix of subscribePath OR
// subscribePath is a prefix of setPath. Use empty path '' to listen to everything.

import type { IntentName, IntentPayloadMap } from './intents';

export type SubscribeCallback = (next: unknown, path: string) => void;

type DispatchArgs<K extends IntentName> = IntentPayloadMap[K] extends void
  ? [intent: K]
  : [intent: K, payload: IntentPayloadMap[K]];

export interface StoreCtx<TState extends object> {
  get(path: string): unknown;
  set(path: string, value: unknown): void;
  dispatch<K extends IntentName>(...args: DispatchArgs<K>): void;
  readonly state: TState;
}

type IntentHandler<K extends IntentName> = (payload: IntentPayloadMap[K], ctx: StoreCtx<never>) => void;

export interface Store<TState extends object> {
  get(): TState;
  get(path: ''): TState;
  get(path: string): unknown;
  set(path: string, value: unknown): void;
  subscribe(path: string, cb: SubscribeCallback): () => void;
  dispatch<K extends IntentName>(...args: DispatchArgs<K>): void;
  onIntent<K extends IntentName>(intent: K, fn: IntentHandler<K>): () => void;
  readonly state: TState;
}

type AnyRecord = Record<string, unknown>;

export function createStore<TState extends object>(initial: TState): Store<TState> {
  const state = clone(initial) as TState;
  const subs = new Map<string, Set<SubscribeCallback>>();
  const intents = new Map<string, Set<(payload: unknown, ctx: StoreCtx<TState>) => void>>();

  function get(path?: string): unknown {
    if (!path) return state;
    const keys = path.split('.');
    let v: unknown = state;
    for (const k of keys) {
      if (v == null || typeof v !== 'object') return undefined;
      v = (v as AnyRecord)[k];
    }
    return v;
  }

  function set(path: string, value: unknown): void {
    const keys = path.split('.');
    const last = keys.pop();
    if (last === undefined) return;
    let obj: AnyRecord = state as unknown as AnyRecord;
    for (const k of keys) {
      const cur = obj[k];
      if (cur == null || typeof cur !== 'object') {
        const next: AnyRecord = {};
        obj[k] = next;
        obj = next;
      } else {
        obj = cur as AnyRecord;
      }
    }
    const cur = obj[last];
    if (value && typeof value === 'object' && !Array.isArray(value) && cur && typeof cur === 'object') {
      Object.assign(cur as AnyRecord, value as AnyRecord);
    } else {
      obj[last] = value;
    }
    notify(path);
  }

  function notify(path: string): void {
    for (const [subPath, set_] of subs) {
      if (matches(path, subPath)) {
        for (const cb of set_) {
          try { cb(get(subPath), subPath); } catch (e) { console.error('[store] subscriber error', e); }
        }
      }
    }
  }

  function subscribe(path: string, cb: SubscribeCallback): () => void {
    let s = subs.get(path);
    if (!s) { s = new Set(); subs.set(path, s); }
    s.add(cb);
    return () => { s.delete(cb); if (s.size === 0) subs.delete(path); };
  }

  const ctx: StoreCtx<TState> = {
    get,
    set,
    dispatch,
    get state(): TState { return state; },
  };

  function dispatch<K extends IntentName>(...args: DispatchArgs<K>): void {
    const [intent, payload] = args as [K, IntentPayloadMap[K]];
    const handlers = intents.get(intent);
    if (!handlers) return;
    for (const h of handlers) {
      try { h(payload, ctx); } catch (e) { console.error(`[store] intent ${intent} error`, e); }
    }
  }

  function onIntent<K extends IntentName>(intent: K, fn: IntentHandler<K>): () => void {
    let h = intents.get(intent);
    if (!h) { h = new Set(); intents.set(intent, h); }
    h.add(fn as (payload: unknown, ctx: StoreCtx<TState>) => void);
    return () => {
      h!.delete(fn as (payload: unknown, ctx: StoreCtx<TState>) => void);
      if (h!.size === 0) intents.delete(intent);
    };
  }

  return {
    get: get as Store<TState>['get'],
    set,
    subscribe,
    dispatch,
    onIntent,
    get state(): TState { return state; },
  };
}

function matches(setPath: string, subPath: string): boolean {
  if (setPath === subPath) return true;
  if (subPath === '') return true;
  if (setPath === '') return true;
  return setPath.startsWith(subPath + '.') || subPath.startsWith(setPath + '.');
}

function clone<T>(o: T): T {
  if (o == null || typeof o !== 'object') return o;
  if (Array.isArray(o)) return (o.map(clone) as unknown) as T;
  const out: AnyRecord = {};
  for (const k of Object.keys(o as object)) out[k] = clone((o as AnyRecord)[k]);
  return out as T;
}
